import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { VendorSourceChip } from '@/components/ui/citation-chip';
import { StepGuide, WorkspaceButton, stepStates, type GuideStep } from '@/components/ui/workspace';
import {
  reviewCount,
  useKbReview,
  useKbSummary,
  useKbVendor,
  type KbReview,
  type KbSummary,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { B, Chip, Fact, Facts, Page, Section, SectionHead, SummaryLine } from './kb-bits';
import { fmt, formatDate, platformLabel, plural } from './kb-logic';
import {
  DomainStepForm,
  DomainsFact,
  PartnersFact,
  ProductStepForm,
  ProductsFact,
} from './catalog-edit';
import { ReviewSection } from './review/review-section';
import { KnowledgeSection } from './knowledge/knowledge-section';
import { AnswersSection } from './answers/answers-section';
import { SourcesSection } from './sources/sources-section';
import { SourcesReviewGroups } from './review/sources-review';
import { useKb } from './vendor-knowledge-screen';
import { VendorPageProvider, useVendorPage, type SourceFormKind } from './vendor-page-context';

/**
 * One vendor, read top to bottom (prototype `vendorView()`): the header
 * with its domains, products and partners; the summary line; for a vendor
 * with nothing known yet, the steps that fill it; To review (curators);
 * Knowledge; Requirement answers; Sources.
 */
export function VendorPage({ vendorId }: { vendorId: string }) {
  const { canCurate, scrollTo } = useKb();
  const vendor = useKbVendor(vendorId);
  const summary = useKbSummary({ vendor_id: vendorId });
  const review = useKbReview(vendorId, canCurate);
  const [form, setForm] = React.useState<SourceFormKind | null>(null);
  const pageCtx = React.useMemo(
    () => ({
      form,
      openForm: (kind: SourceFormKind | null) => {
        setForm(kind);
        if (kind) scrollTo('kb-sources');
      },
    }),
    [form, scrollTo],
  );

  if (vendor.isPending || summary.isPending) {
    return (
      <Page>
        <Spinner />
      </Page>
    );
  }
  if (vendor.isError || summary.isError) {
    return (
      <Page>
        <ErrorMessage error={vendor.error ?? summary.error} />
      </Page>
    );
  }
  const data = vendor.data;
  const counts = summary.data;
  const fresh = counts.knowledge_items === 0 && counts.requirement_answers === 0;

  const received = reviewCount(review.data, 'received');
  const waiting = reviewCount(review.data, 'waiting_to_read');
  const problems = reviewCount(review.data, 'source_problems');

  return (
    <VendorPageProvider value={pageCtx}>
      <Page>
        <VendorHeader data={data} fresh={fresh} />
        <VendorSummary data={data} summary={counts} review={canCurate ? review.data : undefined} />
        {fresh && canCurate ? (
          <StartGuide data={data} summary={counts} review={review.data} />
        ) : null}
        {canCurate ? (
          <ReviewSection
            vendorId={vendorId}
            review={review.data}
            extraCount={received + waiting + problems}
            extraGroups={
              <SourcesReviewGroups
                data={data}
                received={received}
                waiting={waiting}
                problems={problems}
              />
            }
          />
        ) : null}
        {!fresh ? <KnowledgeSection vendor={data} summary={counts} /> : null}
        {counts.requirement_answers > 0 ? (
          <AnswersSection
            scope={{ kind: 'vendor', id: vendorId }}
            summary={counts}
            products={data.products}
          />
        ) : null}
        {canCurate ? <SourcesSection data={data} /> : null}
      </Page>
    </VendorPageProvider>
  );
}

function VendorHeader({ data, fresh }: { data: KbVendor; fresh: boolean }) {
  // Market descriptions came from the Caliber v1 catalog, one per product.
  const described = data.products.filter(
    (p) => p.status === 'active' && p.market_description?.trim(),
  );
  const distinct = Array.from(new Set(described.map((p) => p.market_description!.trim())));
  return (
    <header>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="m-0 text-h1 font-semibold text-ink-900">{data.vendor.name}</h1>
        <Chip tone="neutral" size="md">
          {platformLabel(data.vendor.platform_type)}
        </Chip>
      </div>
      {distinct.length === 0 ? (
        <p className="mb-[18px] mt-2 max-w-[760px] text-body-sm text-ink-500">
          Added on {formatDate(data.vendor.created_at)}.
        </p>
      ) : distinct.length === 1 ? (
        <p className="mb-[18px] mt-2 max-w-[760px] text-[15px] leading-6 text-ink-700">
          {distinct[0]}{' '}
          {described.some((p) => p.market_description_origin === 'v1_import') ? (
            <VendorSourceChip kind="avero_kb" className="ml-1" />
          ) : null}
        </p>
      ) : (
        <div className="mb-[18px] mt-2 max-w-[760px] space-y-1 text-[15px] leading-6 text-ink-700">
          {described.map((p) => (
            <p key={p.id} className="m-0">
              <b className="font-semibold text-ink-900">{p.name}</b>: {p.market_description}
            </p>
          ))}
          {described.some((p) => p.market_description_origin === 'v1_import') ? (
            <VendorSourceChip kind="avero_kb" />
          ) : null}
        </div>
      )}
      <Facts>
        <Fact label="Web domains">
          <DomainsFact data={data} fresh={fresh} />
        </Fact>
        <Fact label="Products">
          <ProductsFact data={data} fresh={fresh} />
        </Fact>
        <Fact label="Implementation partners">
          <PartnersFact data={data} />
        </Fact>
      </Facts>
    </header>
  );
}

function VendorSummary({
  data,
  summary,
  review,
}: {
  data: KbVendor;
  summary: KbSummary;
  review: KbReview | undefined;
}) {
  const toReview = review?.by_vendor.find((v) => v.vendor_id === data.vendor.id)?.count ?? 0;
  const attention =
    reviewCount(review, 'received') +
    reviewCount(review, 'waiting_to_read') +
    reviewCount(review, 'source_problems');
  const parts: React.ReactNode[] = [
    summary.knowledge_items > 0 ? (
      <span>
        <B>{fmt(summary.knowledge_items)}</B> {summary.knowledge_items === 1 ? 'item' : 'items'}{' '}
        confirmed
      </span>
    ) : (
      <span>No knowledge yet</span>
    ),
  ];
  if (review) {
    parts.push(
      <span>
        <B>{fmt(toReview)}</B> to review
      </span>,
      <span>
        <B>{fmt(attention)}</B> {attention === 1 ? 'source needs' : 'sources need'} attention
      </span>,
    );
  }
  parts.push(
    <span>
      {summary.last_checked_at
        ? `Last checked ${formatDate(summary.last_checked_at)}`
        : 'No page checked yet'}
    </span>,
  );
  return <SummaryLine parts={parts} />;
}

/** A vendor with nothing known yet: the steps that fill it, the one to do now in orange. */
function StartGuide({
  data,
  summary,
  review,
}: {
  data: KbVendor;
  summary: KbSummary;
  review: KbReview | undefined;
}) {
  const { openForm } = useVendorPage();
  const domains = data.domains.filter((d) => d.status === 'active').map((d) => d.host);
  const products = data.products.filter((p) => p.status === 'active');
  const proposals = reviewCount(review, 'proposed');
  const pages = summary.web_pages?.registered ?? 0;
  const states = stepStates([
    domains.length > 0,
    products.length > 0,
    pages > 0,
    summary.knowledge_items > 0,
  ]);
  const steps: GuideStep[] = [
    {
      key: 'domains',
      title: 'Add the vendor’s web domains',
      state: states[0]!,
      body: domains.length
        ? `Its pages on ${domains.join(', ')} are captured without extra approval.`
        : 'Pages on the vendor’s own domains are captured without extra approval. Pages elsewhere need yours, one by one.',
      action: <DomainStepForm data={data} />,
    },
    {
      key: 'products',
      title: 'Add its products',
      state: states[1]!,
      body: products.length
        ? `${products.map((p) => p.name).join(', ')}.`
        : 'Each item is filed under a product and one of its modules.',
      action: <ProductStepForm data={data} />,
    },
    {
      key: 'pages',
      title: 'Capture its public pages',
      state: states[2]!,
      body: pages
        ? `${plural(pages, 'web page')} registered.`
        : 'Search for the vendor’s product pages, or add a page you know, under Sources. Reading uses the firm AI budget.',
      action: (
        <span className="flex flex-wrap gap-2">
          <WorkspaceButton variant="primary" onClick={() => openForm('find')}>
            Find pages
          </WorkspaceButton>
          <WorkspaceButton onClick={() => openForm('web')}>Add a web page</WorkspaceButton>
        </span>
      ),
    },
    {
      key: 'confirm',
      title: 'Confirm what AI proposes',
      state: states[3]!,
      body:
        proposals > 0
          ? `${plural(proposals, 'proposal')} ${proposals === 1 ? 'is' : 'are'} waiting below, each quoting the page word for word. Nothing is used until you confirm it.`
          : 'AI reads each capture in full and proposes items, each quoting the source word for word. Nothing is used until you confirm it.',
    },
  ];
  return (
    <Section label="Fill this vendor">
      <SectionHead
        title="Nothing known yet"
        hint="These steps fill it. You can also ask the vendor for documents with an upload link, under Sources."
      />
      <StepGuide aria-label="Fill this vendor" steps={steps} />
    </Section>
  );
}
